import { use_mouse_position_tracker } from '@/pages/_base/shortcut_hint/_hooks/use_mouse_position_tracker';
import { EScreenZone } from '@/pages/_base/shortcut_hint/e_screen_zone';
import { local_ui_hint_shortcut_hide } from '@/state/ui/hint_shortcut/local_ui_hint_shortcut_hide';
import { local_ui_hint_shortcut_show } from '@/state/ui/hint_shortcut/local_ui_hint_shortcut_show';
import { assert_string_not_empty, assert_type_enum } from '@shared/asserts/utils_asserts';
import { EShortcutType } from '@shared/dto/hint_shortcuts/e_shortcut_type';
import { useRef, useState } from 'react';
import { use_dispatch } from '../_base/use_dispatch';
import { use_effect } from '../_base/use_effect';
import { use_ref_updating } from '../_base/use_ref_updating';
import { use_slice_ui, use_slice_user } from '../store/use_slices';

export interface HintShortcutResult {
  on_user_uses_component: VoidFunction;
  on_user_uses_shortcut: VoidFunction;
  on_user_hovered_component: VoidFunction;
  try_hide_hint_shortcut: VoidFunction;
  show_hint_shortcut_in_zone: (target_zone: EScreenZone) => void;
  zone_current: EScreenZone;
}

/*
 * CONSTANTS
 */

/*
 * IMPLEMENTATION
 */

export function use_hint_shortcut(key: EShortcutType) {
  assert_type_enum(key, EShortcutType);

  let dispatch = use_dispatch();

  assert_string_not_empty(key);

  const ref_timeout_id_hide = useRef(NaN);
  const ref_timeout_id_show = useRef(NaN);

  const { mouse_position } = use_mouse_position_tracker();

  const [zone_current, set_zone_current] = useState(EScreenZone.STUB);

  const { is_authorized, user_settings } = use_slice_user(['is_authorized', 'user_settings']);
  const { hints_shortcut_visible } = use_slice_ui(['hints_shortcut_visible']);

  const ref_hints_shortcut_visible = use_ref_updating(hints_shortcut_visible);

  /*
   * EFFECTS
   */
  use_effect(() => {
    return () => try_hide_hint_shortcut();
  });

  use_effect(() => {
    if (zone_current === mouse_position.zone) return;

    try_hide_hint_shortcut();
  }, [mouse_position.zone, key, is_authorized]);

  /*
   * EVENTS
   */

  function on_user_hovered_component() {
    if (key === EShortcutType.STUB) return; //this key is only for stub

    try_show_hint();
  }

  function on_user_uses_shortcut() {
    if (key === EShortcutType.STUB) return; //this key is only for stub

    try_hide_hint_shortcut();
  }

  function on_user_uses_component() {
    if (key === EShortcutType.STUB) return; //this key is only for stub
  }

  /*
   * METHODS
   */

  function try_show_hint() {
    if (key === EShortcutType.STUB) return; //this key is only for stub
    if (!is_authorized) return;
    if (hints_shortcut_visible.includes(key)) return; //already visible
    if (!user_settings.ui_hints_shortcuts_enabled) return; //disabled by settings

    dispatch(local_ui_hint_shortcut_show(key));
  }

  function show_hint_shortcut_in_zone(target_zone: EScreenZone) {
    set_zone_current(target_zone);

    try_show_hint();
  }

  function try_hide_hint_shortcut() {
    clearTimeout(ref_timeout_id_show.current);
    clearTimeout(ref_timeout_id_hide.current);

    if (key === EShortcutType.STUB) return; //this key is only for stub
    if (!is_authorized) return;
    if (!ref_hints_shortcut_visible.current.includes(key)) return; //already hidden

    dispatch(local_ui_hint_shortcut_hide(key));
  }

  let out: HintShortcutResult = {
    on_user_uses_component,
    on_user_uses_shortcut,
    on_user_hovered_component,
    try_hide_hint_shortcut,

    show_hint_shortcut_in_zone,
    zone_current,
  };

  return out;
}
